import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useEffect, useState } from "react";
import CustomInput from "../../inputs/customInput";
import StreetService from "../../../services/streetService";
import CityDropdown from "../../dropdowns/cityDropdown";
import { useTheme } from "@emotion/react";

const UpdateStreetBox = ({
    handleReload,
    setPopupMsg,
    setPopupType,
    setShowPopup,
    street }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [formValues, setFormValues] = useState({
        name: street.name,
        cityId: street.city && street.city.id
    });

    const [selectedCity, setSelectedCity] = useState(street.city);
    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (street) {
            setFormValues({
                name: street.name || '',
                cityId: street.city && street.city.id
            });
            setSelectedCity(street.city);
            setUpdateErrors({});
        }
    }, [street]);

    const validateForm = () => {
        const newUpdateErrors = {};

        setUpdateErrors(newUpdateErrors);

        return Object.keys(newUpdateErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await StreetService.updateStreet({
                    id: street.id,
                    ...formValues
                });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('update');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({ name: '' });
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                p: isTablet ? 2 : 4,
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                    {t('updateStreet')}
                </Typography>
            </Box>
            <CityDropdown
                helperText={updateErrors.city}
                error={!!updateErrors.city}
                setFormValues={setFormValues}
                isFilter={false}
                selectedCity={selectedCity || null}
                setSelectedCity={setSelectedCity}
            />


            <CustomInput
                label={t('name')}
                placeholder={t('nameHere')}
                name="name"
                type='text'
                value={formValues.name}
                error={!!updateErrors.name}
                helperText={updateErrors.name}
                onChange={handleChange}
            />

            {updateErrors.general && (
                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                    {updateErrors.general}
                </Typography>
            )}

            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                mt: updateErrors.general ? 2 : 4
            }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                >
                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <p>{t('update')}</p>
                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                        </Box>
                    }
                </Button>
            </Box>
        </Box>
    );
}

export default UpdateStreetBox;
